import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import {
  Delete,
  Visibility,
  CloudDownload,
  Email,
  Article,
} from '@mui/icons-material';
import Table, { TableActions } from 'components/Table';
import AlertDialog from 'components/AlertDialog';
import PageContainer from 'components/PageContainer';
import { useAllocations } from 'hooks/data/allocation/useAllocations';
import {
  createDeploymentUpdateAddRoute,
  createDeploymentUpdateViewRoute,
  createViewAllocationRoute,
  createViewDraftAllocationRoute,
} from 'adminConstants/routes';
import { SortOrder } from 'further-types/utils';

import { exportToCustodian } from '@redux/actions/UploadAllocations';
import { useUploadAllocations } from 'hooks/data/allocation/useUploadAllocations';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import {
  dateToLabel,
  numberToCurrencyString,
  getCompanyLabel,
  numberToDisplayString,
} from 'further-ui/utils';
import { useDebounce } from 'usehooks-ts';
import { useGetRole } from 'hooks/ui/useGetRole';
import FiltersAndActions from './FiltersAndActions';
import { useAllocation } from 'hooks/data/allocation/useAllocation';
import { useNotification } from 'hooks/ui/useNotification';
import PageContent from 'components/PageContent';
import { take } from 'lodash';
import { EIS1Download } from 'further-ui/components/SVGIcons';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const useStyles = makeStyles()((theme) => ({
  customButton: {
    minWidth: '35px',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.white,
    },
  },
  dFlex: {
    display: 'flex',
  },
  addRoleBtn: {
    textAlign: 'right',
  },
  tableMt: {
    marginTop: 10,
  },
  flxCenter: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  textColor: {
    color: '#282525',
    marginBottom: '15px',
  },
  content: {
    fontFamily: 'MaisonNeue',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '22px',
    textAlign: 'center',
    color: '#7B7B7B',
  },
  sectionAction: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '42px',
    marginBottom: '40px',
  },
  dialog: {
    maxWidth: '624px!important',
  },
}));

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Upload shareholding', isActive: true },
];

const UploadSheetInvestment: React.FC = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const deleteDisclosure = useDisclosure();
  const { firmId: usersFirmId, isSuperAdmin } = useGetRole();
  const { downloadEIS1Certificate } = useUploadAllocations();
  const [canDeleteAllocations, canEditAllocations, canCreateDeploymentUpdate] =
    useHasPermissions([
      RolePermission.DeleteAllocationUpload,
      RolePermission.EditAllocationUpload,
      RolePermission.CreateReportDeployment,
    ]);

  const pagination = usePagination({
    orderBy: 'date',
    order: SortOrder.Desc,
    id: 'uploaded-shareholding',
  });
  const { filters, handleChangeFilters } = useFilters('uploaded-shareholding', {
    defaultFilters: {
      companyName: '',
      firmId: usersFirmId,
      dateRange: {
        startDate: null,
        endDate: null,
      },
    },
    onFiltersChange: pagination.toFirstPage,
  });
  const debouncedSearch = useDebounce(filters.companyName, 500);

  const { delete: allocationDelete } = useAllocation({});
  const { error } = useNotification();

  const draftAllocationDelete =
    api.eisAllocations.deleteDraftAllocation.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Draft allocation deleted successfully.',
        onSuccess: () => {
          deleteDisclosure.onClose();
        },
      }),
    );
  const allocations = useAllocations({
    params: {
      page: pagination.page === 0 ? 1 : pagination.page,
      perPage: pagination.rowsPerPage,
      type: pagination.order,
      keyName: pagination.orderBy,
      search: debouncedSearch,
      firmId: filters.firmId,
      startDate: filters.dateRange?.startDate,
      endDate: filters.dateRange?.endDate,
      excludeInvestmentCount: true,
    },
  });

  const columns = [
    {
      label: 'Date',
      key: 'date',
      render: (elm) => dateToLabel(elm?.date),
    },
    ...(isSuperAdmin
      ? [{ label: 'Firm', key: 'firm', render: (elm) => elm?.firmId?.firmName }]
      : []),
    {
      label: 'Company',
      key: 'company',
      render: (elm) => getCompanyLabel(elm.companyId),
    },
    {
      label: 'Tranche(s)',
      key: 'fundNames',
      render: (elm) => (
        <>
          {take(elm.fundNames, 2).map((fundName, index) => (
            <span key={index}>
              <>{fundName}</>
              {elm.fundNames.length !== 1 && index !== 1 ? ', ' : ''}
              {elm.fundNames.length > 2 && index === 1
                ? ` and ${elm.fundNames.length - 2} more.`
                : ''}
            </span>
          ))}
        </>
      ),
    },

    {
      label: 'Shares',
      key: 'totalNumberOfShare',
      render: (elm) => numberToDisplayString(elm.totalNumberOfShare),
      sort: false,
    },
    {
      label: 'Total investment',
      key: 'totalInvestmentAmount',
      render: (elm) => numberToCurrencyString(elm.totalInvestmentAmount),
      sort: false,
    },
    {
      label: 'Status',
      key: 'status',
      render: (elm) => (
        <>
          {elm.isDraft
            ? 'Draft'
            : elm.isShareConsiderationAllocation
            ? 'Completed (share consideration)'
            : elm.isAutoAllocation
            ? 'Completed (auto-allocation)'
            : 'Completed (upload)'}
        </>
      ),
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (elm) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              label: canEditAllocations ? 'View and edit' : 'View',
              icon: Visibility,
              color: 'primary',
              link: elm.isDraft
                ? createViewDraftAllocationRoute(elm?.draftAllocationId)
                : createViewAllocationRoute(elm?._id),
            },
            {
              label: 'Download',
              icon: CloudDownload,
              color: 'primary',
              visible: !elm.isDraft,
              onClick: () => handleDownload(elm._id, elm.companyId, elm.date),
            },
            {
              label: 'Delete',
              icon: Delete,
              color: 'error',
              visible: canDeleteAllocations,
              disabled:
                elm.isShareConsiderationAllocation || elm.hasKiCertificate,
              tooltip: elm.isShareConsiderationAllocation
                ? 'An allocation as a result of a share consideration exit cannot be deleted. Please delete the relevant exit to remove this allocation.'
                : elm.hasKiCertificate
                ? 'Allocation can not be deleted while an EIS5 relating to this allocation has been created. Please delete the relevant EIS5 and try again.'
                : undefined,
              onClick: () => deleteDisclosure.stageAction(elm),
            },
            {
              label: 'Send deployment update',
              icon: Email,
              color: 'primary',
              visible:
                canCreateDeploymentUpdate &&
                !elm?.isDeploymentUpdateSent &&
                !elm?.isDraft,
              link: createDeploymentUpdateAddRoute(
                elm?._id,
                elm?.companyId?._id,
              ),
            },
            {
              label: 'View deployment update',
              icon: Article,
              color: 'primary',
              visible:
                elm?.isDeploymentUpdateSent && elm?.deploymentUpdates?.[0]?._id,
              link: createDeploymentUpdateViewRoute(
                elm?.deploymentUpdates?.[0]?._id,
              ),
            },
            {
              label: 'Download EIS1 data sheet',
              icon: EIS1Download,
              onClick: () => downloadEIS1Certificate.mutate({ id: elm._id }),
              disabled: elm.isDraft,
              tooltip: elm.isDraft
                ? 'The EIS1 data sheet can be downloaded once the allocation is complete.'
                : undefined,
              color: 'primary',
            },
          ]}
        />
      ),
    },
  ];

  const handleDownload = (id, companyId, allocationDate) => {
    const companyTradingName = companyId.tradingName ?? '';
    dispatch(
      exportToCustodian(
        id,
        { error },
        companyTradingName ?? '',
        allocationDate,
      ),
    );
  };

  const handleConfirmDelete = () => {
    allocationDelete.mutate(
      {
        id: deleteDisclosure.actionProps._id,
      },
      {
        onSuccess: () => {
          if (allocations?.data?.uploadShareHoldings?.length === 1) {
            pagination.pageBack();
          } else {
            allocations.refetch();
          }
        },
        onSettled: () => {
          deleteDisclosure.onClose();
        },
      },
    );
  };

  const handleConfirmDeleteDraft = async () => {
    await draftAllocationDelete.mutateAsync({
      draftAllocationId: deleteDisclosure.actionProps.draftAllocationId,
    });
  };

  const isRecordWithFinalDeal = () => {
    const { actionProps } = deleteDisclosure;
    if (!actionProps?.includesFinalDeal) return false;
    return true;
  };

  return (
    <PageContainer
      heading="Allocations: Allocation history"
      breadcrumbs={breadcrumbs}
    >
      <PageContent>
        <FiltersAndActions
          classes={classes}
          filters={filters}
          handleChangeFilters={handleChangeFilters}
          pagination={pagination}
        />
        <div className={classes.tableMt}>
          <Table
            loading={allocations?.isFetching}
            columns={columns}
            onRequestSort={pagination.handleRequestSort}
            order={pagination.order}
            orderBy={pagination.orderBy}
            tablebody={allocations?.data?.uploadShareHoldings || []}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
            page={pagination.page}
            rowsPerPage={pagination.rowsPerPage}
            count={allocations?.data?.total || 0}
            pagination={true}
            variant="nohover"
          />
        </div>
      </PageContent>
      <AlertDialog
        onClose={deleteDisclosure.onClose}
        onConfirm={
          deleteDisclosure.actionProps?.isDraft
            ? handleConfirmDeleteDraft
            : handleConfirmDelete
        }
        open={deleteDisclosure.isOpen}
        title="Confirm delete"
        content={
          <p>
            <strong>Please note:</strong> This cannot be undone. We recommend
            you download a record of this allocation's results before you
            proceed.
            {deleteDisclosure.actionProps?.hasFundsWithLiveDraftAllocation && (
              <>
                {' '}
                As there is a live draft allocation including one or more
                tranches in this allocation, deletion will also delete that
                draft allocation.
              </>
            )}
            {isRecordWithFinalDeal() && (
              <>
                <br /> <br />
                This was the final deal for some tranches. If you delete this
                allocation, it will automatically reverse the applicable
                tranches' final deal status.
              </>
            )}
          </p>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Delete',
        }}
        confirmBtnProps={{
          disabled:
            allocationDelete.isPending || draftAllocationDelete.isPending,
          loading:
            allocationDelete.isPending || draftAllocationDelete.isPending,
        }}
        cancelBtnProps={{ variant: 'outlined', color: 'primary' }}
      />
    </PageContainer>
  );
};

export default UploadSheetInvestment;
